import { isInCreditWorkflow, isReceipted } from './reconciliationHealth'
import { lineBlocksGrFullAccept } from './goodsReceiptingActionHelpers'
import { getReceiptingRowKey } from './receiptingRowKey'
import { parseActualDeliveryDate } from './actualDeliveryDate'
import { isVoidedGoodsReceiptingOrder } from './goodsReceiptingVoidHelpers'

export const BULK_MARK_RECEIVED_LABEL = 'Mark selected orders as received'

export const BULK_RESULT = {
  SUCCESS: 'success',
  SKIPPED_ALREADY_RECEIVED: 'skipped_already_received',
  SKIPPED_VOIDED: 'skipped_voided',
  SKIPPED_CREDIT_WORKFLOW: 'skipped_credit_workflow',
  SKIPPED_NEEDS_REVIEW: 'skipped_needs_review',
  FAILED: 'failed',
}

/**
 * Venue-local calendar day for an order (ordered date preferred).
 * @returns {string|null} YYYY-MM-DD
 */
export function getOrderVenueLocalYmd(order = {}, dateBasis = 'orderedDate') {
  const raw =
    dateBasis === 'actualDeliveryDate'
      ? order?.actualDeliveryDate || order?.orderedDate || order?.createdAt
      : order?.orderedDate || order?.createdAt || order?.actualDeliveryDate
  const parsed = parseActualDeliveryDate(raw)
  if (parsed.ok) return parsed.ymd
  if (!raw) return null
  // Fallback for ISO timestamps parseActualDeliveryDate may reject mid-string
  const str = String(raw)
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(str)
  return match ? match[1] : null
}

/**
 * Strict cutoff: orders dated *before* cutoffYmd (excludes the cutoff day).
 * Example: cutoff 2026-10-01 → includes 2026-09-30, excludes 2026-10-01.
 */
export function isOrderBeforeCutoffDate(
  order,
  cutoffYmd,
  { dateBasis = 'orderedDate' } = {},
) {
  if (!cutoffYmd) return true
  const ymd = getOrderVenueLocalYmd(order, dateBasis)
  if (!ymd) return false
  return ymd < String(cutoffYmd)
}

/**
 * List-level eligibility for bulk "mark as received" selection.
 * Final short-line checks happen at process time after products load.
 */
export function isEligibleForBulkMarkReceived(order = {}) {
  if (!order?.purchaseOrderNumber || !order?.supplier?._id) return false
  if (isVoidedGoodsReceiptingOrder(order)) return false
  if (isReceipted(order)) return false
  if (isInCreditWorkflow(order)) return false
  if ((order?.receiptingStatus || '') === 'CN Requested') return false
  return true
}

export function getBulkMarkReceivedDisabledReason(order = {}) {
  if (!order?.purchaseOrderNumber || !order?.supplier?._id) {
    return 'Order is missing supplier or PO number.'
  }
  if (isVoidedGoodsReceiptingOrder(order)) {
    return 'Voided orders cannot be bulk-received.'
  }
  if (isReceipted(order)) {
    return 'Already receipted on Goods Receipting.'
  }
  if (
    isInCreditWorkflow(order) ||
    (order?.receiptingStatus || '') === 'CN Requested'
  ) {
    return 'Orders in credit workflow need individual review.'
  }
  return null
}

export function filterEligibleBulkMarkReceivedOrders(orders = []) {
  return (orders || []).filter(isEligibleForBulkMarkReceived)
}

/**
 * Resolve actual delivery date for a PO without inventing one.
 * Prefers an explicit bulk stamp, then the order's existing date.
 */
export function resolveBulkActualDeliveryDate(order = {}, bulkStampYmd = null) {
  if (bulkStampYmd) {
    const parsed = parseActualDeliveryDate(bulkStampYmd)
    return parsed.ok ? parsed.ymd : null
  }
  const existing = parseActualDeliveryDate(order?.actualDeliveryDate)
  return existing.ok ? existing.ymd : null
}

/**
 * Decide process outcome from loaded products before calling create.
 * @returns {{ ok: true } | { ok: false, code: string, reason: string }}
 */
export function evaluateBulkMarkReceivedProducts(order = {}, productsBundle) {
  if (isVoidedGoodsReceiptingOrder(order)) {
    return {
      ok: false,
      code: BULK_RESULT.SKIPPED_VOIDED,
      reason: 'Order is voided.',
    }
  }
  if (isReceipted(order)) {
    return {
      ok: false,
      code: BULK_RESULT.SKIPPED_ALREADY_RECEIVED,
      reason: 'Already receipted.',
    }
  }
  if (
    isInCreditWorkflow(order) ||
    (order?.receiptingStatus || '') === 'CN Requested'
  ) {
    return {
      ok: false,
      code: BULK_RESULT.SKIPPED_CREDIT_WORKFLOW,
      reason: 'In credit workflow.',
    }
  }

  const lines = productsBundle?.orders
  if (!Array.isArray(lines) || lines.length === 0) {
    return {
      ok: false,
      code: BULK_RESULT.SKIPPED_NEEDS_REVIEW,
      reason: 'Could not load order lines for receipting.',
    }
  }

  if (lines.some(lineBlocksGrFullAccept)) {
    return {
      ok: false,
      code: BULK_RESULT.SKIPPED_NEEDS_REVIEW,
      reason:
        'Has short / problem lines — needs individual Report Error / Save receipt.',
    }
  }

  // Partially closed lines (already have a status) need manual review —
  // bulk must not overwrite existing receipt quantities or discrepancies.
  if (
    lines.some(
      line =>
        line?.status !== undefined &&
        line?.status !== null &&
        String(line.status).trim() !== '' &&
        String(line.status).trim() !== 'Not Saved',
    )
  ) {
    return {
      ok: false,
      code: BULK_RESULT.SKIPPED_NEEDS_REVIEW,
      reason: 'Partially receipted — needs individual review.',
    }
  }

  return { ok: true }
}

export function buildBulkFullAcceptPayload(lines = [], placedVia = '') {
  return (lines || []).map(item => ({
    order: item._id,
    supplier: item.supplier,
    purchaseOrderNumber: item.purchaseOrderNumber,
    status: 'Full accept',
    askForCredit: false,
    placedVia,
  }))
}

export function summarizeBulkMarkReceivedResults(results = []) {
  const summary = {
    total: results.length,
    success: 0,
    skippedAlreadyReceived: 0,
    skippedVoided: 0,
    skippedCreditWorkflow: 0,
    needsReview: 0,
    failed: 0,
    items: results,
  }

  results.forEach(item => {
    switch (item?.code) {
      case BULK_RESULT.SUCCESS:
        summary.success += 1
        break
      case BULK_RESULT.SKIPPED_ALREADY_RECEIVED:
        summary.skippedAlreadyReceived += 1
        break
      case BULK_RESULT.SKIPPED_VOIDED:
        summary.skippedVoided += 1
        break
      case BULK_RESULT.SKIPPED_CREDIT_WORKFLOW:
        summary.skippedCreditWorkflow += 1
        break
      case BULK_RESULT.SKIPPED_NEEDS_REVIEW:
        summary.needsReview += 1
        break
      case BULK_RESULT.FAILED:
      default:
        summary.failed += 1
        break
    }
  })

  return summary
}

export function getBulkOrderSelectionKey(order) {
  return getReceiptingRowKey(order)
}

export function countVenuesInOrders(orders = []) {
  const ids = new Set()
  ;(orders || []).forEach(order => {
    const id = order?.venue?._id || order?.venue
    if (id) ids.add(String(id))
  })
  return ids.size
}
